import { useState, useCallback, useEffect, useMemo } from 'react';
import { Link, Outlet, useLocation, useMatch } from 'react-router-dom';
import { rememberPath } from '@/lib/last-path';
import Sidebar from './Sidebar';
import { TopProgressBar } from '../ui';
import { consoleNavGroups } from '../navigation/console-nav';
import { SpaceSwitcher } from '../navigation/SpaceSwitcher';
import { useAuth } from '@/store/auth-context';
import { useMeSpaces } from '@/hooks/useMeSpaces';
import { resolveDefaultSpaceId } from '@/lib/default-space';
import { SidebarCollapseProvider } from './sidebar-collapse-context';
import { SidebarToggleHotkey } from '../keyboard/SidebarToggleHotkey';
import {
  PageHeaderProvider,
  usePageBackLink,
} from '@/contexts/PageHeaderContext';

const SIDEBAR_COLLAPSED_KEY = 'sidebar-collapsed';

export default function ConsoleLayout() {
  const { pathname, search } = useLocation();
  const { user, updatePreferences } = useAuth();
  const { data: spacesData } = useMeSpaces();

  // Pull the active spaceId from the URL when we're inside the spaces subtree.
  const spaceMatch = useMatch('/c/s/:spaceId/*');
  const urlSpaceId = spaceMatch?.params.spaceId;

  // For cross-space pages (Events, Settings) the nav still wants a space so
  // the entity links resolve. Pick the same one ConsoleDefaultSpaceRedirect
  // lands on: last-visited → personal → first available.
  const fallbackSpaceId = useMemo(
    () =>
      resolveDefaultSpaceId({
        spaces: spacesData?.items ?? [],
        lastSpaceId: user?.preferences?.lastSpaceId,
        defaultSpaceId: user?.defaultSpaceId,
      }),
    [spacesData?.items, user?.preferences?.lastSpaceId, user?.defaultSpaceId],
  );
  const navSpaceId = urlSpaceId ?? fallbackSpaceId ?? '';

  useEffect(() => {
    rememberPath('console', pathname + search);
    if (urlSpaceId) {
      rememberPath(`console:${urlSpaceId}`, pathname + search);
    }
  }, [pathname, search, urlSpaceId]);

  const isSandbox = /^\/c\/s\/[^/]+\/sandbox(\/.*)?$/.test(pathname);
  const isPersistentPage = isSandbox;

  // Persist the active space whenever the URL puts us inside one. Mirrors
  // how `lastTopicConversationId` is written from OperatorPage so the next
  // /console visit lands the user back where they were.
  useEffect(() => {
    if (!urlSpaceId) return;
    if (user?.preferences?.lastSpaceId === urlSpaceId) return;
    updatePreferences({ lastSpaceId: urlSpaceId }).catch(() => {});
  }, [urlSpaceId, user?.preferences?.lastSpaceId, updatePreferences]);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true',
  );

  const setCollapsed = useCallback((next: boolean) => {
    setSidebarCollapsed(next);
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
  }, []);

  const handleToggle = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      return next;
    });
  }, []);

  const collapseValue = useMemo(
    () => ({
      collapsed: sidebarCollapsed,
      setCollapsed,
      toggle: handleToggle,
    }),
    [sidebarCollapsed, setCollapsed, handleToggle],
  );

  const navGroups = useMemo(
    () => consoleNavGroups(navSpaceId, user?.role),
    [navSpaceId, user?.role],
  );

  return (
    <SidebarCollapseProvider value={collapseValue}>
    <SidebarToggleHotkey />
    <PageHeaderProvider>
    <div className="min-h-screen bg-background">
      <TopProgressBar />
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={handleToggle}
        groups={navGroups}
        brand="console"
        belowBrand={
          <SpaceSwitcher
            currentSpaceId={navSpaceId}
            currentPath={pathname}
            collapsed={sidebarCollapsed}
          />
        }
      />
      <div
        className="transition-all duration-500 ease-in-out"
        style={{ paddingLeft: sidebarCollapsed ? '4rem' : '16rem' }}
      >
        {/* The space subtree's persistent Sandbox is rendered inside
            SpaceProviderRoute (App.tsx) so its `useSpaceId()` resolves.
            ConsoleLayout only stacks the main content area here; the
            sandbox surface paints over the same column. */}
        {!isPersistentPage && (
          <div className="px-6">
            <PageHeaderSlot />
            <main className="pt-6 pb-6">
              <Outlet />
            </main>
          </div>
        )}
        {isPersistentPage && <Outlet />}
      </div>
    </div>
    </PageHeaderProvider>
    </SidebarCollapseProvider>
  );
}

/**
 * Reserved 64px row above `<main>`. Always takes vertical space so the
 * page title's Y position is unaffected by whether a page registered a
 * back link or not. Vertically centres the link so it Y-aligns with the
 * sidebar's brand mark.
 */
function PageHeaderSlot() {
  const backLink = usePageBackLink();
  return (
    <div className="flex h-12 items-end pb-1">
      {backLink && (
        <Link
          to={backLink.to}
          className="inline-flex items-center text-base text-muted-foreground hover:text-foreground"
        >
          ← {backLink.label}
        </Link>
      )}
    </div>
  );
}
